import * as React from "react";
import { CheckCircle2, Clock, Shield, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The banded state message that sits above a deliverable, an offer or a payout.
 *
 * It states what is true right now and what happens next — never a bare status
 * word. "Publicado" is a label; "Publicado, verificado y pagado — el dinero
 * salió de la garantía" is a banner. If the sentence you are about to write
 * would fit on a badge, it belongs on a badge.
 */
const TONES = {
  success: {
    box: "bg-success-bg border-success-border",
    title: "text-success-strong",
    body: "text-success-fg",
    Icon: CheckCircle2,
  },
  warning: {
    box: "bg-warning-bg border-warning-border",
    title: "text-warning-fg",
    body: "text-warning-fg",
    Icon: Clock,
  },
  changes: {
    box: "bg-changes-bg border-changes-border",
    title: "text-changes-fg",
    body: "text-changes-fg",
    Icon: ShieldAlert,
  },
  info: {
    box: "bg-steel-50 border-steel-100",
    title: "text-steel-700",
    body: "text-steel-600",
    Icon: Shield,
  },
  danger: {
    box: "bg-danger-bg border-danger-border",
    title: "text-danger-fg",
    body: "text-danger-fg",
    Icon: ShieldAlert,
  },
} as const;

export interface StatusBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: keyof typeof TONES;
  /** Shadows the DOM `title` attribute on purpose — this is the banner's headline. */
  title?: React.ReactNode;
  /** Pass `false` to drop the glyph, or a node to override it. */
  icon?: boolean | React.ReactNode;
  action?: React.ReactNode;
}

export function StatusBanner({
  tone = "info",
  title,
  icon = true,
  action,
  className,
  children,
  ...props
}: StatusBannerProps) {
  const t = TONES[tone];
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-control border px-3.5 py-3",
        t.box,
        className,
      )}
      {...props}
    >
      {icon !== false && (
        <span className={cn("mt-px shrink-0", t.title)}>
          {icon === true ? <t.Icon className="size-[15px]" strokeWidth={2.2} /> : icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {title && <p className={cn("text-card-body font-bold", t.title)}>{title}</p>}
        {children && (
          <p className={cn("text-fine leading-normal", title && "mt-0.5", t.body)}>
            {children}
          </p>
        )}
      </div>
      {action && <span className="shrink-0">{action}</span>}
    </div>
  );
}
